import { useState, useEffect, useMemo, ChangeEvent, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import useMediaQuery from '@mui/material/useMediaQuery';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Alert from '@mui/material/Alert';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import { TableSurveys } from './components/TableSurveys';
import { TableRecordings } from './components/TableRecordings';
import { TableTeamOfTeams } from './components/TableTeamOfTeams';
import { TableTeams } from './components/TableTeams';
import { TableRmet } from './components/TableRmet';
import { Survey } from './components/TableSurveysRow';
import { Recording } from './components/TableRecordingsRow';
import { TeamOfTeams } from './components/TableTeamOfTeamsRow';
import { Team } from './components/TableTeamsRow';
import { Cohort } from './components/TableCohortsRow';
import { CohortDateEmailConfig, CohortEmailConfigs } from './components/CohortEmailDialog';
import { AssessmentAnalysisData, AssessmentAnalysis } from './components/AssessmentAnalysis';
import { RelationalAnalysisData, RelationalAnalysis } from './components/RelationalAnalysis';
import { AssessmentReport } from './components/AssessmentReport';
import { RelationalReport } from './components/RelationalReport';
import { PlaybookAnalysisData, PlaybookAnalysis } from './components/PlaybookAnalysis';
import {
  cognitoSignIn,
  cognitoCompleteNewPassword,
  cognitoSignOut,
  getCognitoToken,
  cognitoForgotPassword,
  cognitoConfirmPassword,
} from './components/CognitoAuth';

import './App.css';
import { Typography, IconButton, Tooltip, Select, MenuItem } from '@mui/material';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import TeamOfTeamsAnalysis, { TeamOfTeamsAnalysisData } from './components/TeamOfTeamsAnalysis';
import HelpDrawer from './components/HelpDrawer';
import UsersDrawer from './components/UsersDrawer';
import OnboardingTour, { ONBOARDING_SEEN_KEY } from './components/OnboardingTour';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import { Dayjs } from 'dayjs';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const API_URL = process.env.REACT_APP_API_URL ?? '';

/**
 * Temporary allowlist for the RMET admin tab while the feature is being piloted.
 * The backend enforces the same list (`RMET_ALLOWED_EMAILS` in `teamei.ts`) —
 * hiding the tab alone would not stop anyone calling the operations directly.
 * Remove both when RMET opens up to other users.
 */
const RMET_ALLOWED_EMAILS = ['michelangelo.pagliarini@gmail.com', 'massimiliano.ghini2@unibo.it'];

export type ApiFetch = (body: Record<string, unknown>) => Promise<Response>;

const hasCachedSession = (): boolean => {
  const clientId = process.env.REACT_APP_COGNITO_APP_CLIENT_ID;
  if (!clientId) return false;
  return localStorage.getItem(`CognitoIdentityServiceProvider.${clientId}.LastAuthUser`) !== null;
};

const validatePassword = (pw: string) => ({
  length: pw.length >= 12,
  digit: /\d/.test(pw),
  uppercase: /[A-Z]/.test(pw),
});
const isPasswordValid = (pw: string) => Object.values(validatePassword(pw)).every(Boolean);

function App() {
  const { t, i18n } = useTranslation();
  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('lang', lang);
  };
  const [mode, setMode] = useState<'dark' | 'light'>(
    () => (localStorage.getItem('theme') as 'dark' | 'light') ?? 'dark'
  );
  const theme = useMemo(() => {
    const PRIMARY_COLOR = process.env.REACT_APP_API_URL?.endsWith('dev')
      ? mode === 'dark'
        ? '#57C4FF'
        : '#011984'
      : mode === 'dark'
        ? '#B44DE1'
        : '#5D0185';
    return createTheme({
      palette: {
        mode,
        primary: { main: PRIMARY_COLOR },
        secondary: { main: '#53A2BE' },
      },
      typography: {
        fontFamily: `"Raleway", sans-serif`,
        fontSize: 14,
        fontWeightLight: 300,
        fontWeightRegular: 400,
        fontWeightMedium: 500,
      },
    });
  }, [mode]);
  const toggleMode = () => {
    const next = mode === 'dark' ? 'light' : 'dark';
    localStorage.setItem('theme', next);
    setMode(next);
  };
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordRequired, setNewPasswordRequired] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [resetCodeSent, setResetCodeSent] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [loggedIn, setLoggedIn] = useState(hasCachedSession());
  const [authChecking, setAuthChecking] = useState(hasCachedSession());
  const [userEmail, setUserEmail] = useState('');
  const [assessmentAnalysis, setAssessmentAnalysis] = useState<AssessmentAnalysisData | null>(null);
  const [relationalAnalysis, setRelationalAnalysis] = useState<RelationalAnalysisData | null>(null);
  const [playbookAnalysis, setPlaybookAnalysis] = useState<PlaybookAnalysisData | null>(null);
  const [teamOfTeamsAnalysis, setTeamOfTeamsAnalysis] = useState<TeamOfTeamsAnalysisData | null>(
    null
  );
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [retrievalSurveysInProgress, setRetrievalSurveysInProgress] = useState(false);
  const [retrievalRecordingsInProgress, setRetrievalRecordingsInProgress] = useState(false);
  const [retrievalTeamOfTeamsInProgress, setRetrievalTeamOfTeamsInProgress] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [loadingSurveyId, setLoadingSurveyId] = useState<string | number>(0);
  const [loadingParticipants, setLoadingParticipants] = useState<string | number>(0);
  const [loginError, setLoginError] = useState<boolean>(false);
  /** LimeSurvey rejected the server credentials; the survey list can't be loaded. */
  const [surveysUnavailable, setSurveysUnavailable] = useState<boolean>(false);
  const [surveyAnalyzed, setSurveyAnalyzed] = useState<Survey | null>(null);
  const [participantsBySurvey, setParticipantsBySurvey] = useState<{
    [key: string]: { [key: string]: string };
  }>({});
  const [leaderBySurvey, setLeaderBySurvey] = useState<{
    [key: string]: { [key: string]: string };
  }>({});
  const [loadingLeader, setLoadingLeader] = useState<string | number>(0);
  const [tabValue, setTabValue] = useState(fullScreen ? 1 : 0);
  const [analyzeSurveysLoading, setAnalyzeSurveysLoading] = useState<boolean>(false);
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [teamOfTeams, setTeamOfTeams] = useState<TeamOfTeams[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamsLoading, setTeamsLoading] = useState<boolean>(false);
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [cohortsLoading, setCohortsLoading] = useState<boolean>(false);
  const [helpDrawerOpen, setHelpDrawerOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  /** RMET is limited to named pilot users for now; see RMET_ALLOWED_EMAILS. */
  const canSeeRmet = RMET_ALLOWED_EMAILS.includes(userEmail.trim().toLowerCase());
  const [usersDrawerOpen, setUsersDrawerOpen] = useState(false);
  const [tourRun, setTourRun] = useState(false);

  const handleTabChange = (event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    if (loggedIn && localStorage.getItem(ONBOARDING_SEEN_KEY) === null) {
      setTourRun(true);
    }
  }, [loggedIn]);

  const finishTour = () => {
    localStorage.setItem(ONBOARDING_SEEN_KEY, '1');
    setTourRun(false);
  };

  /** Read email and admin role from the ID token payload. */
  const applyTokenClaims = (token: string) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setUserEmail(payload.email ?? '');
      const groups: string[] = payload['cognito:groups'] ?? [];
      setIsAdmin(groups.includes('admins'));
    } catch {}
  };

  useEffect(() => {
    const init = async () => {
      const token = await getCognitoToken();
      if (token) {
        applyTokenClaims(token);
        setAuthChecking(false);
        await listSurveys();
      } else {
        setLoggedIn(false);
        setAuthChecking(false);
      }
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const apiFetch: ApiFetch = async (body) => {
    const token = await getCognitoToken();
    if (!token) {
      setLoggedIn(false);
      throw new Error('Not authenticated');
    }
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      body: JSON.stringify(body),
    });
    if (response.status === 401 || response.status === 403) {
      // Gateway auth failures (expired/invalid token) end the session; app-level
      // permission denials from the Lambda carry an `error` field and must not.
      const data = await response
        .clone()
        .json()
        .catch(() => null);
      if (!data?.error) setLoggedIn(false);
    }
    return response;
  };

  const login = async () => {
    setLoginError(false);
    setLoginLoading(true);
    try {
      const token = await cognitoSignIn(email, password);
      applyTokenClaims(token);
      await listSurveys();
    } catch (err: unknown) {
      if (err instanceof Error && err.message === 'NEW_PASSWORD_REQUIRED') {
        setNewPasswordRequired(true);
      } else {
        setLoginError(true);
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const completeNewPassword = async () => {
    setLoginError(false);
    setLoginLoading(true);
    try {
      const token = await cognitoCompleteNewPassword(newPassword);
      setNewPasswordRequired(false);
      applyTokenClaims(token);
      await listSurveys();
    } catch {
      setLoginError(true);
    } finally {
      setLoginLoading(false);
    }
  };

  const sendResetCode = async () => {
    setLoginError(false);
    setLoginLoading(true);
    try {
      await cognitoForgotPassword(email);
      setResetCodeSent(true);
    } catch {
      setLoginError(true);
    } finally {
      setLoginLoading(false);
    }
  };

  const confirmReset = async () => {
    setLoginError(false);
    setLoginLoading(true);
    try {
      await cognitoConfirmPassword(email, resetCode, resetNewPassword);
      setForgotPasswordMode(false);
      setResetCodeSent(false);
      setResetCode('');
      setResetNewPassword('');
    } catch {
      setLoginError(true);
    } finally {
      setLoginLoading(false);
    }
  };

  const logout = async () => {
    cognitoSignOut();
    setLoggedIn(false);
    setUserEmail('');
    setIsAdmin(false);
  };

  const getLeaderAndReportDate = async (surveyId: string | number) => {
    setLoadingLeader(surveyId);
    try {
      const response = await apiFetch({
        surveyId: surveyId.toString(),
        operation: 'get_leader_report_date',
      });

      if (response.ok) {
        let jsonResponse = await response.json();
        const out = jsonResponse['output'] ?? {};
        // Normalise leader from string[] to a comma-separated string for display
        if (Array.isArray(out.leader)) {
          out.leader = out.leader.join(', ');
        }
        setLeaderBySurvey((prev) => ({
          ...prev,
          [surveyId.toString()]: out,
        }));
        setLoadingLeader(0);
      } else {
        console.error(response);
        setLoadingLeader(0);
      }
    } catch (error) {
      console.error('An error occurred:', error);
      setLoadingLeader(0);
    }
  };

  const editLeaderReportDate = async (
    survey: Survey,
    leaderMail: string,
    reportDate: Dayjs | null,
    reminderDate: Dayjs | null
  ) => {
    try {
      const leaderArray = leaderMail
        .split(',')
        .map((e) => e.trim())
        .filter(Boolean);
      const response = await apiFetch({
        operation: 'edit_leader_report_date',
        surveyId: survey['i'],
        leader: leaderArray,
        reportDate: reportDate ? reportDate.format('YYYY-MM-DD') : null,
        reminderDate: reminderDate ? reminderDate.format('YYYY-MM-DD') : null,
      });

      if (response.ok) {
        setLeaderBySurvey((prev) => ({
          ...prev,
          [survey['i'].toString()]: {
            leader: leaderMail,
            reportDate: reportDate ? reportDate.format('YYYY-MM-DD') : '',
            reminderDate: reminderDate ? reminderDate.format('YYYY-MM-DD') : '',
          },
        }));
      } else {
        console.error(response);
      }
    } catch (error) {
      console.error('An error occurred:', error);
    }
  };

  const createSurvey = async (
    originalSurveyId: string,
    newTitle: string,
    leaderMail: string,
    reportDate: Dayjs | null,
    reminderDate: Dayjs | null,
    /** Executive Session only: mailed the survey link by the backend at creation. */
    participants: string[] = [],
    surveyType?: string
  ): Promise<unknown> => {
    const leaderArray = leaderMail
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean);
    const response = await apiFetch({
      operation: 'create_survey',
      originalSurveyId,
      teamName: newTitle,
      leaderMail: leaderArray,
      reportDate: reportDate?.format('YYYY-MM-DD'),
      reminderDate: reminderDate?.format('YYYY-MM-DD'),
      participants,
      surveyType,
    });
    if (!response.ok) throw new Error('create_survey failed');
    const json = await response.json();
    return json['output'];
  };

  const multipleCreateSurvey = async (data: Array<unknown>): Promise<unknown[]> => {
    // Normalise leaderMail from comma-separated string to string[] for the backend
    const normalisedData = (data as Array<any>).map((d) => ({
      ...d,
      leaderMail:
        typeof d.leaderMail === 'string'
          ? d.leaderMail
              .split(',')
              .map((e: string) => e.trim())
              .filter(Boolean)
          : (d.leaderMail ?? []),
    }));
    const response = await apiFetch({
      operation: 'multiple_create_survey',
      data: normalisedData,
    });
    if (!response.ok) throw new Error('multiple_create_survey failed');
    const json = await response.json();
    return json['output'];
  };

  /**
   * Mails the Executive Session invites. Returns the backend outcome so the row can report
   * partial failures; the caller updates the survey's invitesSentAt on success.
   */
  const sendExecutiveInvites = async (
    surveyId: string | number,
    surveyTitle: string
  ): Promise<{ sent: number; failed: string[]; invitesSentAt?: string }> => {
    const response = await apiFetch({
      operation: 'send_executive_invites',
      surveyId: surveyId.toString(),
      surveyTitle,
      lang: i18n.language,
    });
    if (!response.ok) throw new Error('send_executive_invites failed');
    const json = await response.json();
    const output = json['output'];
    if (output['invitesSentAt']) {
      setSurveys((prev) =>
        prev.map((s) =>
          s.i.toString() === surveyId.toString()
            ? { ...s, invitesSentAt: output['invitesSentAt'] }
            : s
        )
      );
    }
    return output;
  };

  const getParticipants = async (surveyId: string | number) => {
    setLoadingParticipants(surveyId);
    try {
      const response = await apiFetch({
        surveyId: surveyId.toString(),
        operation: 'get_participants',
      });

      if (response.ok) {
        let jsonResponse = await response.json();
        setParticipantsBySurvey((prev) => ({
          ...prev,
          [surveyId.toString()]: jsonResponse['output'],
        }));
        setLoadingParticipants(0);
      } else {
        console.error(response);
        setLoadingParticipants(0);
      }
    } catch (error) {
      console.error('An error occurred:', error);
      setLoadingParticipants(0);
    }
  };

  const analyzeSurveys = async (surveys: Survey[], name: string) => {
    setAnalyzeSurveysLoading(true);
    try {
      const response = await apiFetch({
        surveys: surveys,
        name: name,
        operation: 'analyze_surveys',
        lang: i18n.language,
      });

      if (response.ok) {
        let jsonResponse = await response.json();
        setSurveyAnalyzed(null);
        setAssessmentAnalysis(null);
        setRelationalAnalysis(null);
        setPlaybookAnalysis(null);
        setMessage(null);
        const newTeamOfTeams = { ...jsonResponse['output'], id: jsonResponse['output']['name'] };
        setTeamOfTeamsAnalysis(jsonResponse['output']['analysis']);
        setOpen(true);
        setTeamOfTeams([newTeamOfTeams, ...teamOfTeams]);
        setAnalyzeSurveysLoading(false);
      } else {
        console.error(response);
        setAnalyzeSurveysLoading(false);
      }
    } catch (error) {
      console.error('An error occurred:', error);
      setAnalyzeSurveysLoading(false);
    }
  };

  const analyzeSurvey = async (surveyId: string | number) => {
    const survey = surveys.filter((s) => s.i === surveyId)[0];
    setSurveyAnalyzed(survey);
    setLoadingSurveyId(surveyId);
    setAssessmentAnalysis(null);
    setRelationalAnalysis(null);
    setPlaybookAnalysis(null);
    setTeamOfTeamsAnalysis(null);
    setMessage(null);
    try {
      const response = await apiFetch({
        surveyId: surveyId.toString(),
        surveyTitle: survey?.t,
        operation: 'analyze',
        lang: i18n.language,
      });

      if (response.ok) {
        let jsonResponse = await response.json();
        setOpen(true);
        if (
          jsonResponse['output'] === 'No answers yet' ||
          jsonResponse['output'] === 'Survey is not active'
        ) {
          setMessage(jsonResponse['output']);
        } else {
          if (jsonResponse['output']['reportType'] === 'TeamOfTeams') {
            // Executive Session: the backend reports the single survey as a team of teams,
            // one pseudo-team per respondent.
            setTeamOfTeamsAnalysis(jsonResponse['output']['analysis']);
          } else if ('senso' in jsonResponse['output']) {
            setPlaybookAnalysis(jsonResponse['output']);
          } else if (!('Costruire' in (jsonResponse['output']['norme'] ?? {}))) {
            setRelationalAnalysis({ ...jsonResponse['output'], surveyId: surveyId });
          } else {
            setAssessmentAnalysis({ ...jsonResponse['output'], surveyId: surveyId });
          }
        }
        setLoadingSurveyId(0);
      } else {
        console.error(response);
        setLoadingSurveyId(0);
      }
    } catch (error) {
      console.error('An error occurred:', error);
      setLoadingSurveyId(0);
    }
  };

  const listTeamOfTeams = async () => {
    setTeamOfTeams([]);
    setRetrievalTeamOfTeamsInProgress(true);
    try {
      const response = await apiFetch({ operation: 'list_team_of_teams' });

      if (response.ok) {
        let jsonResponse = await response.json();
        setTeamOfTeams(jsonResponse['output']);
        setRetrievalTeamOfTeamsInProgress(false);
      } else {
        console.error(response);
        setRetrievalTeamOfTeamsInProgress(false);
      }
    } catch (error) {
      console.error('An error occurred:', error);
      setRetrievalTeamOfTeamsInProgress(false);
    }
  };

  const listTeams = async () => {
    setTeamsLoading(true);
    try {
      const response = await apiFetch({ operation: 'list_teams' });
      if (response.ok) {
        const jsonResponse = await response.json();
        setTeams(jsonResponse['output']);
      } else {
        console.error(response);
      }
    } catch (error) {
      console.error('An error occurred:', error);
    } finally {
      setTeamsLoading(false);
    }
  };

  const createTeam = async (name: string, leader: string): Promise<Team> => {
    const leaderArray = leader
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean);
    const response = await apiFetch({
      operation: 'create_team',
      teamName: name,
      teamLeader: leaderArray,
    });
    const jsonResponse = await response.json();
    return jsonResponse['output'] as Team;
  };

  const editTeam = async (teamId: string, name: string, leader: string): Promise<void> => {
    const leaderArray = leader
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean);
    await apiFetch({ operation: 'edit_team', teamId, teamName: name, teamLeader: leaderArray });
  };

  const deleteTeam = async (teamId: string, deleteSurveys: boolean): Promise<void> => {
    await apiFetch({ operation: 'delete_team', teamId, deleteSurveys });
    if (deleteSurveys) {
      const team = teams.find((t) => t.id === teamId);
      if (team) setSurveys((prev) => prev.filter((s) => !team.surveyIds.includes(String(s.i))));
    }
  };

  const addSurveyToTeam = async (teamId: string, surveyId: string): Promise<void> => {
    await apiFetch({ operation: 'add_survey_to_team', teamId, surveyId });
  };

  const removeSurveyFromTeam = async (teamId: string, surveyId: string): Promise<void> => {
    await apiFetch({ operation: 'remove_survey_from_team', teamId, surveyId });
  };

  const listCohorts = async () => {
    setCohortsLoading(true);
    try {
      const response = await apiFetch({ operation: 'list_cohorts' });
      if (response.ok) {
        const jsonResponse = await response.json();
        setCohorts(jsonResponse['output']);
      } else {
        console.error(response);
      }
    } catch (error) {
      console.error('An error occurred:', error);
    } finally {
      setCohortsLoading(false);
    }
  };

  const createCohort = async (name: string, systemLeader: string): Promise<Cohort> => {
    const response = await apiFetch({
      operation: 'create_cohort',
      cohortName: name,
      cohortSystemLeader: systemLeader,
    });
    const jsonResponse = await response.json();
    return jsonResponse['output'] as Cohort;
  };

  const editCohort = async (
    cohortId: string,
    name: string,
    systemLeader: string,
    dates: string[],
    dateNames?: string[]
  ): Promise<void> => {
    await apiFetch({
      operation: 'edit_cohort',
      cohortId,
      cohortName: name,
      cohortSystemLeader: systemLeader,
      cohortDates: dates,
      cohortDateNames: dateNames,
    });
  };

  const deleteCohort = async (cohortId: string): Promise<void> => {
    await apiFetch({ operation: 'delete_cohort', cohortId });
  };

  const addSurveyToCohort = async (cohortId: string, surveyId: string): Promise<void> => {
    await apiFetch({ operation: 'add_survey_to_cohort', cohortId, surveyId });
  };

  const removeSurveyFromCohort = async (cohortId: string, surveyId: string): Promise<void> => {
    await apiFetch({ operation: 'remove_survey_from_cohort', cohortId, surveyId });
  };

  const assignTeamToCohort = async (teamId: string, cohortId: string): Promise<void> => {
    await apiFetch({ operation: 'assign_team_to_cohort', teamId, cohortId });
  };

  const removeTeamFromCohort = async (teamId: string): Promise<void> => {
    await apiFetch({ operation: 'remove_team_from_cohort', teamId });
  };

  const editCohortEmailConfig = async (
    cohortId: string,
    dateIndex: string,
    config: CohortDateEmailConfig
  ): Promise<void> => {
    await apiFetch({
      operation: 'edit_cohort_email_config',
      cohortId,
      cohortEmailDateIndex: dateIndex,
      cohortEmailConfig: config,
    });
    setCohorts((prev) =>
      prev.map((c) =>
        c.id === cohortId
          ? { ...c, emailConfigs: { ...(c.emailConfigs ?? {}), [dateIndex]: config } }
          : c
      )
    );
  };

  const uploadCohortAttachment = async (
    _cohortId: string,
    fileName: string,
    fileType: string
  ): Promise<{ uploadUrl: string; fileKey: string }> => {
    const response = await apiFetch({ operation: 'upload_recording', fileName, fileType });
    const json = await response.json();
    return json['output'] as { uploadUrl: string; fileKey: string };
  };

  const sendCohortEmails = async (
    cohortId: string,
    dateIndex: string
  ): Promise<{ sent: number; errors: string[] }> => {
    const response = await apiFetch({
      operation: 'send_cohort_emails',
      cohortId,
      cohortEmailDateIndex: dateIndex,
    });
    const json = await response.json();
    return json['output'] as { sent: number; errors: string[] };
  };

  const getEmailDefaults = async (): Promise<CohortEmailConfigs> => {
    const response = await apiFetch({ operation: 'get_email_defaults' });
    const json = await response.json();
    return (json['output'] ?? {}) as CohortEmailConfigs;
  };

  const setEmailDefault = async (
    dateIndex: string,
    config: CohortDateEmailConfig
  ): Promise<void> => {
    const current = await getEmailDefaults();
    await apiFetch({
      operation: 'set_email_defaults',
      emailDefaults: { ...current, [dateIndex]: config },
    });
  };

  const multipleCreateCohort = async (
    cohorts: Array<unknown>,
    teamsWithoutCohort: Array<unknown>
  ): Promise<{ cohorts: Cohort[]; teams: Team[] }> => {
    // Normalise leaderEmail fields from comma-separated strings to string[] for the backend
    const splitEmail = (e: unknown): string[] =>
      typeof e === 'string'
        ? e
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : ((e as string[]) ?? []);
    const normalisedCohorts = (cohorts as Array<any>).map((c) => ({
      ...c,
      teams: (c.teams ?? []).map((t: any) => ({ ...t, leaderEmail: splitEmail(t.leaderEmail) })),
    }));
    const normalisedTeams = (teamsWithoutCohort as Array<any>).map((t) => ({
      ...t,
      leaderEmail: splitEmail(t.leaderEmail),
    }));
    const response = await apiFetch({
      operation: 'multiple_create_cohort',
      cohorts: normalisedCohorts,
      data: normalisedTeams,
    });
    if (!response.ok) throw new Error('multiple_create_cohort failed');
    const json = await response.json();
    const result = json['output'] as { cohorts: Cohort[]; teams: Team[]; surveys: Survey[] };
    if (result.surveys?.length) {
      setSurveys((prev) => [...result.surveys, ...prev]);
    }
    return result;
  };

  const listRecordings = async () => {
    setRecordings([]);
    setRetrievalRecordingsInProgress(true);
    try {
      const response = await apiFetch({ operation: 'list_recordings', lang: i18n.language });

      if (response.ok) {
        let jsonResponse = await response.json();
        setRecordings(jsonResponse['output']);
        setRetrievalRecordingsInProgress(false);
      } else {
        console.error(response);
        setRetrievalRecordingsInProgress(false);
      }
    } catch (error) {
      console.error('An error occurred:', error);
      setRetrievalRecordingsInProgress(false);
    }
  };

  const listSurveys = async () => {
    setSurveys([]);
    setRetrievalSurveysInProgress(true);
    setRetrievalRecordingsInProgress(true);
    try {
      const response = await apiFetch({ operation: 'list_surveys' });

      if (response.ok) {
        let jsonResponse = await response.json();
        const output = jsonResponse['output'];
        // `list_surveys` answers 200 with the string 'Invalid session key' when
        // LimeSurvey rejects the server credentials. Cognito auth succeeded, so
        // keep the user signed in and flag the survey list as unavailable —
        // calling .reverse() on that string used to throw, and the catch below
        // bounced the user back to the login form with "wrong credentials".
        setSurveysUnavailable(!Array.isArray(output));
        setSurveys(Array.isArray(output) ? output.reverse() : []);
        // The remaining lists come from DynamoDB and work without LimeSurvey.
        await listRecordings();
        await listTeamOfTeams();
        await listTeams();
        await listCohorts();
        setRetrievalSurveysInProgress(false);
        setLoginError(false);
        setLoggedIn(true);
      } else {
        console.error(response);
        setRetrievalSurveysInProgress(false);
      }
    } catch (error) {
      console.error('An error occurred:', error);
      setRetrievalSurveysInProgress(false);
      setLoggedIn(false);
      setLoginError(true);
    }
  };

  const reportMatch = window.location.pathname.match(/^\/report\/(.+)$/);
  if (reportMatch) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AssessmentReport
          surveyId={reportMatch[1]}
          apiFetch={apiFetch}
          mode={mode}
          toggleMode={toggleMode}
        />
      </ThemeProvider>
    );
  }

  const relationalReportMatch = window.location.pathname.match(/^\/relational-report\/(.+)$/);
  if (relationalReportMatch) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <RelationalReport
          surveyId={relationalReportMatch[1]}
          apiFetch={apiFetch}
          mode={mode}
          toggleMode={toggleMode}
        />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className="App">
        {authChecking ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
            }}
          >
            <CircularProgress />
          </Box>
        ) : loggedIn ? (
          <Grid
            container
            spacing={2}
            padding={4}
            direction="row"
            sx={{ justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Typography variant="h5">TeamEI</Typography>
            <Grid
              container
              spacing={2}
              direction="row"
              sx={{ justifyContent: 'flex-end', alignItems: 'center' }}
            >
              <Grid>
                <Typography variant="body2">{t('nav.hello', { email: userEmail })}</Typography>
              </Grid>
              <Grid>
                <Select
                  size="small"
                  value={i18n.language}
                  onChange={(e) => changeLanguage(e.target.value)}
                  variant="standard"
                  disableUnderline
                  sx={{ fontSize: 14 }}
                >
                  <MenuItem value="it">IT</MenuItem>
                  <MenuItem value="en">EN</MenuItem>
                </Select>
              </Grid>
              <Grid>
                <Tooltip title={mode === 'dark' ? t('nav.light_mode') : t('nav.dark_mode')}>
                  <IconButton onClick={toggleMode} color="inherit">
                    {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid>
                <Tooltip title={t('help.nav_button')}>
                  <IconButton
                    data-tour="help-button"
                    onClick={() => setHelpDrawerOpen(true)}
                    color="inherit"
                  >
                    <HelpOutlineIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
              {isAdmin && (
                <Grid>
                  <Tooltip title={t('users.nav_button')}>
                    <IconButton onClick={() => setUsersDrawerOpen(true)} color="inherit">
                      <ManageAccountsIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
              )}
              <Grid>
                <Button variant="outlined" onClick={() => logout()}>
                  {t('nav.logout')}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        ) : (
          <header className="App-header">
            <Grid container spacing={2} padding={4} alignItems="flex-end">
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  id="email"
                  label={t('login.email')}
                  value={email}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  size="small"
                  fullWidth
                />
              </Grid>
              {!forgotPasswordMode && (
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    id="pw"
                    label={t('login.password')}
                    type="password"
                    value={password}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                    size="small"
                    fullWidth
                  />
                </Grid>
              )}
              {newPasswordRequired && (
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    id="new-pw"
                    label={t('login.new_password')}
                    type="password"
                    value={newPassword}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                    size="small"
                    fullWidth
                  />
                </Grid>
              )}
              {forgotPasswordMode && !resetCodeSent && (
                <Grid size={12}>
                  <Alert severity="info">{t('login.first_password_hint')}</Alert>
                </Grid>
              )}
              {resetCodeSent && (
                <>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      id="reset-code"
                      label={t('login.verification_code')}
                      value={resetCode}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setResetCode(e.target.value)}
                      slotProps={{ inputLabel: { shrink: true } }}
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      id="reset-new-pw"
                      label={t('login.new_password')}
                      type="password"
                      value={resetNewPassword}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setResetNewPassword(e.target.value)
                      }
                      slotProps={{ inputLabel: { shrink: true } }}
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid size={12}>
                    <Alert severity="info">{t('login.no_code_hint')}</Alert>
                  </Grid>
                </>
              )}
              {(newPasswordRequired || resetCodeSent) && (
                <Grid size={12}>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 0.5 }}>
                    {(
                      [
                        ['length', t('login.password_criteria_length')],
                        ['digit', t('login.password_criteria_digit')],
                        ['uppercase', t('login.password_criteria_uppercase')],
                      ] as [keyof ReturnType<typeof validatePassword>, string][]
                    ).map(([key, label]) => {
                      const pw = newPasswordRequired ? newPassword : resetNewPassword;
                      const met = validatePassword(pw)[key];
                      const touched = pw.length > 0;
                      return (
                        <Typography
                          key={key}
                          variant="caption"
                          color={!touched ? 'text.secondary' : met ? 'success.main' : 'error.main'}
                        >
                          {!touched ? '○' : met ? '✓' : '✗'} {label}
                        </Typography>
                      );
                    })}
                  </Box>
                </Grid>
              )}
              <Button
                variant="outlined"
                loading={loginLoading}
                disabled={
                  (newPasswordRequired && !isPasswordValid(newPassword)) ||
                  (resetCodeSent && !isPasswordValid(resetNewPassword))
                }
                onClick={() => {
                  if (newPasswordRequired) return completeNewPassword();
                  if (resetCodeSent) return confirmReset();
                  if (forgotPasswordMode) return sendResetCode();
                  return login();
                }}
              >
                {newPasswordRequired
                  ? t('login.set_password')
                  : resetCodeSent
                    ? t('login.reset_password')
                    : forgotPasswordMode
                      ? t('login.send_code')
                      : t('login.login')}
              </Button>
              {!newPasswordRequired && !forgotPasswordMode && (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    setForgotPasswordMode(true);
                    setLoginError(false);
                  }}
                >
                  {t('login.forgot_password')}
                </Button>
              )}
              {forgotPasswordMode && (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    setForgotPasswordMode(false);
                    setResetCodeSent(false);
                    setResetCode('');
                    setResetNewPassword('');
                    setLoginError(false);
                  }}
                >
                  {t('login.back_to_login')}
                </Button>
              )}
            </Grid>
          </header>
        )}
        {loginError && (
          <Alert severity="error">
            {newPasswordRequired || resetCodeSent
              ? t('login.set_password_error')
              : t('login.wrong_credentials')}
          </Alert>
        )}
        {loggedIn && surveysUnavailable && (
          <Alert severity="warning">{t('surveys.error_backend_unavailable')}</Alert>
        )}

        {/* The login page's header only fills the viewport, so anything rendered
            below it is reachable by scrolling (notably on mobile). */}
        {loggedIn && !authChecking && (
          <>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              centered={!fullScreen}
              variant={fullScreen ? 'fullWidth' : 'standard'}
            >
              <Tab label={t('nav.surveys')} data-tour="tab-surveys" />
              <Tab label={t('nav.recordings')} data-tour="tab-recordings" />
              <Tab label={t('nav.team_of_teams')} data-tour="tab-team-of-teams" />
              <Tab label={t('nav.teams')} data-tour="tab-teams" />
              {canSeeRmet && <Tab label={t('nav.rmet')} data-tour="tab-rmet" />}
            </Tabs>

            <HelpDrawer
              open={helpDrawerOpen}
              onClose={() => setHelpDrawerOpen(false)}
              onReplayTour={() => setTourRun(true)}
              // The RMET tab has no help section yet, so the drawer opens collapsed.
              defaultSection={
                (['surveys', 'recordings', 'team_of_teams', 'teams', undefined] as const)[tabValue]
              }
            />
            {isAdmin && (
              <UsersDrawer
                open={usersDrawerOpen}
                onClose={() => setUsersDrawerOpen(false)}
                apiFetch={apiFetch}
                currentUserEmail={userEmail}
                surveys={surveys}
              />
            )}
            <OnboardingTour run={tourRun} onFinish={finishTour} />

            <CustomTabPanel value={tabValue} index={0}>
              <TableSurveys
                rows={surveys}
                analyzeSurvey={analyzeSurvey}
                loading={loadingSurveyId}
                getParticipants={getParticipants}
                participantsBySurvey={participantsBySurvey}
                loadingParticipants={loadingParticipants}
                isMobile={fullScreen}
                analyzeSurveys={analyzeSurveys}
                analyzeSurveysLoading={analyzeSurveysLoading}
                setSurveys={setSurveys}
                getLeaderAndReportDate={getLeaderAndReportDate}
                loadingLeader={loadingLeader}
                leaderBySurvey={leaderBySurvey}
                setLeaderBySurvey={setLeaderBySurvey}
                editLeaderReportDate={editLeaderReportDate}
                createSurvey={createSurvey}
                multipleCreateSurvey={multipleCreateSurvey}
                sendExecutiveInvites={sendExecutiveInvites}
                retrievalInProgress={retrievalSurveysInProgress}
                isAdmin={isAdmin}
              />
              {retrievalSurveysInProgress && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }} padding={4}>
                  <CircularProgress />
                </Box>
              )}
            </CustomTabPanel>
            <CustomTabPanel value={tabValue} index={1}>
              <TableRecordings
                rows={recordings}
                setRecordings={setRecordings}
                apiFetch={apiFetch}
                isAdmin={isAdmin}
              />
              {retrievalRecordingsInProgress && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }} padding={4}>
                  <CircularProgress />
                </Box>
              )}
            </CustomTabPanel>
            <CustomTabPanel value={tabValue} index={2}>
              <TableTeamOfTeams rows={teamOfTeams} />
              {retrievalTeamOfTeamsInProgress && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }} padding={4}>
                  <CircularProgress />
                </Box>
              )}
            </CustomTabPanel>
            <CustomTabPanel value={tabValue} index={3}>
              <TableTeams
                teams={teams}
                setTeams={setTeams}
                cohorts={cohorts}
                setCohorts={setCohorts}
                surveys={surveys}
                loading={teamsLoading || cohortsLoading}
                createTeam={createTeam}
                editTeam={editTeam}
                deleteTeam={deleteTeam}
                addSurveyToTeam={addSurveyToTeam}
                removeSurveyFromTeam={removeSurveyFromTeam}
                createCohort={createCohort}
                editCohort={editCohort}
                deleteCohort={deleteCohort}
                addSurveyToCohort={addSurveyToCohort}
                removeSurveyFromCohort={removeSurveyFromCohort}
                assignTeamToCohort={assignTeamToCohort}
                removeTeamFromCohort={removeTeamFromCohort}
                editCohortEmailConfig={editCohortEmailConfig}
                uploadCohortAttachment={uploadCohortAttachment}
                sendCohortEmails={sendCohortEmails}
                getEmailDefaults={getEmailDefaults}
                setEmailDefault={setEmailDefault}
                isAdmin={isAdmin}
                multipleCreateCohort={multipleCreateCohort}
                analyzeSurvey={analyzeSurvey}
                loadingSurvey={loadingSurveyId}
                getParticipants={getParticipants}
                participantsBySurvey={participantsBySurvey}
                loadingParticipants={loadingParticipants}
                isMobile={fullScreen}
                editLeaderReportDate={editLeaderReportDate}
                getLeaderAndReportDate={getLeaderAndReportDate}
                loadingLeader={loadingLeader}
                leaderBySurvey={leaderBySurvey}
              />
            </CustomTabPanel>
            {canSeeRmet && (
              <CustomTabPanel value={tabValue} index={4}>
                <TableRmet apiFetch={apiFetch} isAdmin={isAdmin} />
              </CustomTabPanel>
            )}
          </>
        )}

        <Dialog
          fullScreen={fullScreen}
          fullWidth={true}
          maxWidth={false}
          open={open}
          onClose={() => {
            setOpen(false);
            setTeamOfTeamsAnalysis(null);
          }}
          aria-labelledby="responsive-dialog-title"
        >
          <DialogTitle>{surveyAnalyzed && surveyAnalyzed.t + ', ' + surveyAnalyzed.i}</DialogTitle>
          <DialogContent>
            {message && (
              <Typography variant="body1" padding={4}>
                {message}
              </Typography>
            )}
            <PlaybookAnalysis
              analysis={playbookAnalysis}
              surveyId={surveyAnalyzed && surveyAnalyzed.i.toString()}
            />
            <AssessmentAnalysis analysis={assessmentAnalysis} apiFetch={apiFetch} />
            <RelationalAnalysis analysis={relationalAnalysis} apiFetch={apiFetch} />
            {teamOfTeamsAnalysis && <TeamOfTeamsAnalysis data={teamOfTeamsAnalysis} />}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpen(false);
                setTeamOfTeamsAnalysis(null);
              }}
            >
              {t('common.close')}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </ThemeProvider>
  );
}

export default App;
